/**
 * 클라이언트로 내려보내는 번역 메시지 범위.
 *
 * 왜: [locale] layout 이 `getMessages()` 전체(ko 약 227KB, 40개 네임스페이스)를
 * NextIntlClientProvider 에 넘기면 모든 페이지 HTML·RSC 페이로드에 그대로 직렬화된다.
 * 크루 콘솔(43KB)·결제(14KB)·예약 상세 같은 비공개 화면 문구가 블로그 글·상품 상세 HTML
 * 에도 실려 있었다(감사 06 #6). 10-16: 회사 소개·고객센터·프로필 보안·리뷰·쿠폰처럼
 * 특정 화면 문구도 라우트 범위로 옮겼다(전역 ko 139KB → 약 70KB).
 *
 * 규칙
 *  - 범위 항목은 경로다 — "crew"(네임스페이스 통째) 또는 "profile.loginSecurity"(하위 트리).
 *  - 전역(layout) Provider = 전체 − ROUTE_SCOPED 경로 − SERVER_ONLY + GLOBAL_SHARED 경로.
 *    범위에 없는 새 네임스페이스는 기본으로 전역에 들어간다(fail-open — next-intl 은
 *    키가 없으면 throw 하지 않고 화면에 키를 그대로 노출하므로, 모르는 것은 내려보내는
 *    편이 안전하다).
 *  - GLOBAL_SHARED_PATHS = 범위로 뺀 네임스페이스 중 전역 컴포넌트(알림 동의·언어 시트·
 *    고객센터 위젯·본인인증 안내 모달)가 쓰는 하위 경로. 전역에 남긴다.
 *  - 범위 경로는 그 라우트 layout 의 `<ScopedIntlProvider scope=…>` 가 **추가분만**
 *    내려보내고, 클라이언트에서 부모 Provider 메시지에 깊은 병합한다
 *    (components/i18n/ScopedMessagesProvider). 범위가 중첩되면(promotions → concert)
 *    병합도 중첩된다. 전역을 다시 실어 보내지 않으므로 상품→상품 같은 클라이언트
 *    이동 RSC 페이로드에 전역 문구가 반복되지 않는다.
 *  - 검증: `src/i18n/__tests__/client-messages.test.ts` 가 import 그래프를 따라 모든
 *    [locale] 페이지의 클라이언트 컴포넌트가 쓰는 useTranslations 네임스페이스(일부만
 *    내려가는 네임스페이스는 t("키") 단위까지)가 그 페이지에 내려가는지 확인한다.
 *    범위를 바꾸면 테스트부터 돌린다.
 */

type Messages = Record<string, unknown>;

/** 서버 컴포넌트(getTranslations)에서만 쓰는 네임스페이스 — 클라이언트로 보내지 않는다. */
export const SERVER_ONLY_NAMESPACES = [
  "busan",
  "station",
  "staging-auth",
  "clusterProducts",
  "searchHere",
  "checkingLocation",
  "optionOnly",
  "modelHub",
  // 하위 경로도 된다 — generateMetadata 전용
  "common.metadata",
] as const;

/**
 * 라우트 범위 → 그 라우트에서만 필요한 메시지 경로.
 * 키 = `src/app/[locale]/` 아래 디렉터리(그 디렉터리의 layout.tsx 가 범위 Provider 를 둔다).
 */
export const ROUTE_MESSAGE_SCOPES = {
  "(crew)/crew": ["crew"],
  checkout: ["checkout", "deliveryAddress", "reservations", "coupon", "cart"],
  cart: ["reservations", "cart"],
  messages: ["messages"],
  profile: [
    "reservations",
    "lTrust",
    "notification-settings",
    "deliveryAddress",
    "profile",
    "reviews",
    "coupon",
    "verificationGuide",
  ],
  "account-deleted": ["profile.loginSecurity.accountDeletedPage"],
  blog: ["blog"],
  "promotions/concert": ["concert"],
  "promotions/[slug]": ["promotion", "promotionSns"],
  "promotions/sns-event": ["promotionSns"],
  "products/[id]": ["reviews", "coupon", "cart"],
  partner: ["partner", "reviews"],
  help: ["help"],
  about: ["about"],
  careers: ["careers"],
  sustainability: ["sustainability"],
  unsubscribed: ["unsubscribe"],
  app: ["appInstallPage"],
  blocked: ["blocked"],
  dev: ["verificationGuide"],
} as const satisfies Record<string, readonly string[]>;

/** 범위로 뺐지만 전역 컴포넌트가 쓰는 하위 경로 — 전역에 남긴다. */
export const GLOBAL_SHARED_PATHS = [
  // NotificationConsentDialog(layout·결제 완료·채팅방)
  "profile.notifications",
  // LanguageDrawer(Navbar)
  "profile.menu.language",
  // SupportFaqTab(고객센터 위젯, layout)
  "help.helpCenter.faq",
  // VerificationGuideModal(VerificationGuideWrapper, layout)
  ...[
    "title",
    "description",
    "warning",
    "stepsTitle",
    "step1Title",
    "step1Desc",
    "step2Title",
    "step2Desc",
    "step3Title",
    "step3Desc",
    "phoneVerification",
    "idVerification",
    "faceVerification",
    "verified",
    "pending",
    "rejected",
    "notVerified",
    "verifyButton",
    "continueButton",
  ].map((k) => `verificationGuide.${k}`),
] as const;

export type MessageScope = keyof typeof ROUTE_MESSAGE_SCOPES;

const ROUTE_SCOPED_PATHS = [...new Set<string>(Object.values(ROUTE_MESSAGE_SCOPES).flat())];
const SERVER_ONLY = new Set<string>(SERVER_ONLY_NAMESPACES);
const SERVER_ONLY_SUBPATHS = SERVER_ONLY_NAMESPACES.filter((p) => p.includes("."));

function isPlainObject(v: unknown): v is Messages {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** "a.b.c" 경로의 값(없으면 undefined). */
export function getMessagePath(messages: Messages, path: string): unknown {
  let cur: unknown = messages;
  for (const seg of path.split(".")) {
    if (!isPlainObject(cur) || !(seg in cur)) return undefined;
    cur = cur[seg];
  }
  return cur;
}

/** target 의 path 에 value 를 둔다 — 지나는 객체는 복사해 원본(getMessages 캐시)을 건드리지 않는다. */
function setPath(target: Messages, source: Messages, path: string, value: unknown): void {
  const segs = path.split(".");
  let t = target;
  let s: unknown = source;
  for (const seg of segs.slice(0, -1)) {
    s = isPlainObject(s) ? s[seg] : undefined;
    const existing = t[seg];
    // 원본과 같은 참조면(아직 복사 전) 얕은 복사로 바꾼다.
    const next: Messages = isPlainObject(existing)
      ? existing === s
        ? { ...existing }
        : existing
      : {};
    t[seg] = next;
    t = next;
  }
  t[segs[segs.length - 1]] = value;
}

/** path 를 지운다(복사-후-삭제). 빈 부모는 남겨 둔다. */
function deletePath(target: Messages, source: Messages, path: string): void {
  const segs = path.split(".");
  if (segs.length === 1) {
    delete target[segs[0]];
    return;
  }
  const parentPath = segs.slice(0, -1).join(".");
  const parent = getMessagePath(target, parentPath);
  if (!isPlainObject(parent)) return;
  const copy = { ...parent };
  delete copy[segs[segs.length - 1]];
  setPath(target, source, parentPath, copy);
}

/** 전역 Provider 에서 (통째로) 빠지는 최상위 네임스페이스인가. */
export function isExcludedFromGlobal(namespace: string): boolean {
  return ROUTE_SCOPED_PATHS.includes(namespace) || SERVER_ONLY.has(namespace);
}

/** [locale] layout 의 전역 Provider 에 넘길 메시지. */
export function pickGlobalClientMessages(messages: Messages): Messages {
  const out: Messages = {};
  for (const [ns, value] of Object.entries(messages)) {
    if (!SERVER_ONLY.has(ns)) out[ns] = value;
  }
  for (const path of [...ROUTE_SCOPED_PATHS, ...SERVER_ONLY_SUBPATHS]) {
    if (getMessagePath(out, path) !== undefined) deletePath(out, messages, path);
  }
  for (const path of GLOBAL_SHARED_PATHS) {
    const value = getMessagePath(messages, path);
    if (value !== undefined) setPath(out, messages, path, value);
  }
  return out;
}

/** 범위 Provider 가 내려보내는 추가분 = 그 범위 경로만(전역은 부모 Provider 에 이미 있다). */
export function pickScopeMessages(messages: Messages, scope: MessageScope): Messages {
  const out: Messages = {};
  for (const path of ROUTE_MESSAGE_SCOPES[scope]) {
    const value = getMessagePath(messages, path);
    if (value !== undefined) setPath(out, messages, path, value);
  }
  return out;
}

/** 깊은 병합 — 객체끼리는 합치고 나머지는 overlay 가 이긴다. 입력은 바꾸지 않는다. */
export function mergeMessages(base: Messages, overlay: Messages): Messages {
  const out: Messages = { ...base };
  for (const [k, v] of Object.entries(overlay)) {
    const b = out[k];
    out[k] = isPlainObject(b) && isPlainObject(v) ? mergeMessages(b, v) : v;
  }
  return out;
}

/** 이 범위의 상위 범위들(자기 포함, 바깥 → 안 순서). 중첩 Provider 병합 순서와 같다. */
export function scopeChain(scope: MessageScope): MessageScope[] {
  return (Object.keys(ROUTE_MESSAGE_SCOPES) as MessageScope[])
    .filter((s) => s === scope || scope.startsWith(`${s}/`))
    .sort((a, b) => a.length - b.length);
}

/** 범위 라우트의 클라이언트가 실제로 보는 메시지 = 전역 + 상위·자기 범위 추가분(테스트·측정용). */
export function pickScopedClientMessages(messages: Messages, scope: MessageScope): Messages {
  let out = pickGlobalClientMessages(messages);
  for (const s of scopeChain(scope)) out = mergeMessages(out, pickScopeMessages(messages, s));
  return out;
}
